import { useState } from "react";
import { useSearchParams } from "react-router";
import { useAngemeldet } from "../../kern/sitzung";
import { usePersonen, personNach } from "../../kern/personen";
import { anzeigename } from "../../kern/namen";
import { fehlertext } from "../../kern/fehler";
import { GruppenSeite } from "../../kern/ui/GruppenSeite";
import { Karte } from "../../kern/ui/Karte";
import { NeueAufgabeKnopf } from "../../kern/ui/NeueAufgabeKnopf";
import { Leer, Meldung } from "../../kern/ui/Leer";
import { Schalter } from "../../kern/ui/Schalter";
import { Segment } from "../../kern/ui/Segment";
import { Wertwahl, type WertOption } from "../../kern/ui/Wertwahl";
import { useAufgaben, useRessortFarben, useRessorts } from "./daten";
import { passt, sortieren, STATUS_OPTIONEN, type Aufgabe, type Status } from "./logik";
import { ortText, ortVon, type Ort } from "./sprintlogik";
import { useSprints } from "./sprints";
import { useSchnellaktionen } from "./aktionen";
import { Rueckfrage } from "./Rueckfrage";
import { AufgabenListe, type NeueZeile } from "./Liste";
import { personGruppe, type ListenVorgabe } from "./vorgaben";
import { useLoeschen } from "./loeschen";
import { laufenderSprint } from "./sprints";
import { useVorhabenUebersicht } from "./vorhaben";
import ui from "../../kern/ui/ui.module.css";
import s from "./aufgaben.module.css";
import { Laedt } from "../../kern/ui/Laedt";

type Gruppierung = "person" | "ort" | "ressort";

const STATUS_FILTER: readonly WertOption<Status | "">[] = [
  { wert: "", text: "Alle Status" },
  ...STATUS_OPTIONEN.map((o) => ({ wert: o.wert, text: o.text, erklaerung: o.erklaerung, ton: o.ton, zeichen: o.zeichen })),
];

const ORT_RANG: Record<Ort, string> = { sprint: "0", backlog: "1", archiv: "2" };
const ORTE: Ort[] = ["sprint", "backlog", "archiv"];

/** Ansicht 05: Alle Aufgaben, gruppiert nach Person, Ort (Sprint, Backlog, Archiv) oder Ressort. Filter Wo, Person, Ressort,
 *  Vorhaben, Status, Nur Hoch. „Offen“ ist diese Ansicht mit Filter „ohne Person“, `?wo=sprint` setzt den Filter Wo (Link im Sprint-Kopf). Gelöschte Aufgaben über einen eigenen Schalter. Zeilen wie im Sprint
 *  (AUFGABEN-LISTE.md): Unteraufgaben unter ▸, eigene Zeile nur, wenn ihre Aufgabe nicht in derselben Gruppe steht. */
export function Alle({ nurOffen = false }: { nurOffen?: boolean }) {
  const sitzung = useAngemeldet();
  const [gruppierung, setGruppierung] = useState<Gruppierung>(nurOffen ? "ort" : "person");
  const [params] = useSearchParams();
  const woAusLink = params.get("wo");
  const [wo, setWo] = useState<Ort | "">(ORTE.find((o) => o === woAusLink) ?? "");
  const [suche, setSuche] = useState("");
  const [nurHoch, setNurHoch] = useState(false);
  // Filter Person: "" alle, "offen" ohne Verantwortliche, sonst „enthält diese Person“
  const [person, setPerson] = useState<string>(nurOffen ? "offen" : "");
  // Filter Ressort: "" alle, "ohne" ohne Ressort, sonst die ID
  const [ressort, setRessort] = useState("");
  // Filter Vorhaben (BAUPLAN-PAKETE.md, Abschnitt 9.5): "" alle, "ohne" ohne Vorhaben, sonst die ID
  const [vorhabenFilter, setVorhabenFilter] = useState("");
  const [status, setStatus] = useState<Status | "">("");
  const [mitErledigt, setMitErledigt] = useState(false);
  const [mitArchiv, setMitArchiv] = useState(false);
  const aufgaben = useAufgaben(mitArchiv);
  const sprints = useSprints();
  const personen = usePersonen();
  const ressorts = useRessorts();
  const farben = useRessortFarben();
  const vorhaben = useVorhabenUebersicht();
  const sa = useSchnellaktionen();
  const loeschen = useLoeschen(sa);

  const alle = aufgaben.data ?? [];
  const gefiltert = sortieren(
    alle.filter(
      (a) =>
        passt(a, suche) &&
        (!nurHoch || a.prioritaet === "hoch") &&
        (!person || (person === "offen" ? a.personen.length === 0 : a.personen.includes(person))) &&
        (!wo || ortVon(a) === wo) &&
        (!ressort || (ressort === "ohne" ? !a.ressort_id : a.ressort_id === ressort)) &&
        (!vorhabenFilter || (vorhabenFilter === "ohne" ? !a.vorhaben_id : a.vorhaben_id === vorhabenFilter)) &&
        (!status || a.status === status) &&
        (mitErledigt || wo === "archiv" || status === "erledigt" || a.status !== "erledigt") &&
        (mitArchiv || !a.archiviert_am),
    ),
  );
  const offenGesamt = alle.filter((a) => a.status !== "erledigt" && !a.archiviert_am).length;

  // Optionen der Filter. Der erste Wert ("") heißt „nicht gefiltert“.
  const woFilter: WertOption<Ort | "">[] = [
    { wert: "", text: "Überall" },
    { wert: "sprint", text: "Sprint" },
    { wert: "backlog", text: "Backlog" },
    ...(nurOffen ? [] : [{ wert: "archiv" as const, text: "Archiv" }]),
  ];
  const personFilter: WertOption<string>[] = [
    { wert: "", text: "Alle Personen" },
    { wert: "offen", text: "Offen (ohne Person)" },
    ...(personen.data ?? []).filter((p) => p.aktiv).map((p) => ({ wert: p.id, text: anzeigename(p) })),
  ];
  const ressortFilter: WertOption<string>[] = [
    { wert: "", text: "Alle Ressorts" },
    { wert: "ohne", text: "Ohne Ressort" },
    ...(ressorts.data ?? []).map((r) => ({ wert: r.id, text: r.name })),
  ];
  const vorhabenFilterOptionen: WertOption<string>[] = [
    { wert: "", text: "Alle Vorhaben" },
    { wert: "ohne", text: "Ohne Vorhaben" },
    ...(vorhaben.data ?? []).map((v) => ({ wert: v.id, text: v.aktiv ? v.name : `${v.name} (archiviert)` })),
  ];
  const filterGesetzt = !!wo || (!nurOffen && !!person) || !!ressort || !!vorhabenFilter || !!status;
  function filterZuruecksetzen() {
    setWo("");
    setPerson(nurOffen ? "offen" : "");
    setRessort("");
    setVorhabenFilter("");
    setStatus("");
  }

  // Nach Person: Eine Aufgabe steht bei jeder ihrer Personen.
  const schluessel = (a: Aufgabe): { id: string; name: string; rang: string }[] => {
    if (gruppierung === "person") {
      if (a.personen.length === 0) return [{ id: "", name: "Ohne Person (Offen)", rang: "0" }];
      return a.personen.map((pid) => {
        const p = personNach(personen.data, pid);
        const name = p ? anzeigename(p) : "…";
        return { id: pid, name, rang: `1${name}` };
      });
    }
    if (gruppierung === "ort") {
      const o = ortVon(a);
      // Archiv: neueste Sprints zuerst
      const ende = sprints.data?.find((x) => x.id === a.sprint_id)?.ende ?? "";
      return [{ id: a.sprint_id ?? "", name: ortText(a), rang: `${ORT_RANG[o]}${o === "archiv" ? String(99999999 - Number(ende.replace(/-/g, "") || 0)) : ""}` }];
    }
    const r = ressorts.data?.find((x) => x.id === a.ressort_id);
    return [r ? { id: r.id, name: r.name, rang: `1${r.name}` } : { id: "", name: "Ohne Ressort", rang: "9" }];
  };
  const gruppen = new Map<string, { id: string; name: string; rang: string; liste: Aufgabe[] }>();
  for (const a of gefiltert) {
    for (const k of schluessel(a)) {
      const g = gruppen.get(k.id) ?? { id: k.id, name: k.name, rang: k.rang, liste: [] };
      g.liste.push(a);
      gruppen.set(k.id, g);
    }
  }
  const sortiert = [...gruppen.values()].sort((a, b) => a.rang.localeCompare(b.rang, "de"));
  const unter = new Map<string, Aufgabe[]>();
  for (const a of alle) if (a.eltern_id && (mitArchiv || !a.archiviert_am)) unter.set(a.eltern_id, [...(unter.get(a.eltern_id) ?? []), a]);
  const laufend = laufenderSprint(sprints.data);
  const gemeinsam = {
    personen: personen.data,
    ressorts: ressorts.data,
    farben: farben.data,
    sa,
    loeschen,
    unterVon: (id: string) => unter.get(id) ?? [],
    sprint: laufend ? { beginn: laufend.beginn, ende: laufend.ende, name: laufend.name } : null,
  };

  /** Zeile „+ Aufgabe hinzufügen“ je Gruppe (BAUPLAN-AUFGABENLISTE.md, Abschnitt 5): Vorgaben wie „Neue Aufgabe“ oben, dazu der Wert der
   *  Gruppe. Mit Suche oder Filter keine, sonst verschwände die neue Aufgabe gleich wieder. Archiv nie, den Sprint nur mit sprint.planen. */
  function neuFuer(id: string): NeueZeile | undefined {
    if (suche.trim() || nurHoch || filterGesetzt) return undefined;
    const basis: ListenVorgabe = nurOffen ? { person: "offen" } : {};
    if (gruppierung === "ort") {
      if (!id) return { vorgabe: { ...basis, wo: "backlog" } };
      return laufend && id === laufend.id && sitzung.rechte.has("sprint.planen") ? { vorgabe: { ...basis, wo: "sprint" } } : undefined;
    }
    if (gruppierung === "ressort") return { vorgabe: { ...basis, ressort: id || null } };
    const p = personGruppe(id, {
      ich: sitzung.person.id,
      vorstand: sitzung.rechte.has("aufgaben.alle_bearbeiten"),
      personIstVorstand: !!personen.data?.find((x) => x.id === id)?.vorstand,
    });
    return p ? { vorgabe: { ...basis, ...p } } : undefined;
  }

  // Eine Seite „Aufgaben“ mit Reitern Alle und Offen, jede behält ihre Adresse (BAUPLAN-SEITENLEISTE.md, Abschnitt 4)
  return (
    <GruppenSeite
      gruppe="Aufgaben"
      text={
        nurOffen
          ? `${gefiltert.length} ohne Person. Wer übernimmt?`
          : `${gefiltert.length} von ${offenGesamt} offenen Aufgaben, gruppiert nach ${{ person: "Person", ort: "Ort", ressort: "Ressort" }[gruppierung]}.`
      }
      aktionen={<NeueAufgabeKnopf vorgabe={nurOffen ? { person: "offen" } : undefined} />}
    >
      <Karte>
        <div className={s.filter}>
          <label className={s.suche}>
            <span className="nur-leser">Aufgabe suchen</span>
            <input className={ui.eingabe} type="search" placeholder="Suchen" value={suche} onChange={(e) => setSuche(e.target.value)} />
          </label>
          <Segment
            legende="Gruppieren nach"
            optionen={[
              { wert: "person", label: "Person" },
              { wert: "ort", label: "Wo" },
              { wert: "ressort", label: "Ressort" },
            ]}
            wert={gruppierung}
            onWechsel={setGruppierung}
          />
        </div>
        {/* Filter als Wertwahl (BAUPLAN-FEINSCHLIFF.md, Abschnitt 3.4): leer weiß mit Namen, gesetzt schwarz mit Wert */}
        <div className={s.filterPillen} role="group" aria-label="Filter">
          <Wertwahl art="Wo" optionen={woFilter} wert={wo} filter={{ leer: "" }} onWechsel={setWo} />
          {!nurOffen ? <Wertwahl art="Person" optionen={personFilter} wert={person} filter={{ leer: "" }} onWechsel={setPerson} /> : null}
          <Wertwahl art="Ressort" optionen={ressortFilter} wert={ressort} filter={{ leer: "" }} onWechsel={setRessort} />
          {vorhaben.data?.length ? <Wertwahl art="Vorhaben" optionen={vorhabenFilterOptionen} wert={vorhabenFilter} filter={{ leer: "" }} onWechsel={setVorhabenFilter} /> : null}
          <Wertwahl art="Status" optionen={STATUS_FILTER} wert={status} filter={{ leer: "" }} onWechsel={setStatus} />
          {filterGesetzt ? (
            <button type="button" className={s.zuruecksetzen} onClick={filterZuruecksetzen}>
              Filter zurücksetzen
            </button>
          ) : null}
        </div>
        <div className={s.schalterReihe}>
          <Schalter label="Nur Hoch" checked={nurHoch} onChange={(e) => setNurHoch(e.target.checked)} />
          <Schalter label="Erledigte" checked={mitErledigt} onChange={(e) => setMitErledigt(e.target.checked)} />
          <Schalter label="Gelöscht" checked={mitArchiv} onChange={(e) => setMitArchiv(e.target.checked)} />
        </div>
        {aufgaben.isPending ? <Laedt /> : null}
        {aufgaben.isError ? <Meldung fehler>{fehlertext(aufgaben.error)}</Meldung> : null}
        {aufgaben.data && gefiltert.length === 0 ? (
          nurOffen && !suche && !nurHoch && !wo && !ressort && !vorhabenFilter && !status ? (
            <Leer titel="Offen ist leer.">Alles hat eine Person. Gut so.</Leer>
          ) : (
            <Leer titel="Keine Treffer.">Prüf die Suche oder die Filter.</Leer>
          )
        ) : null}
      </Karte>
      {sortiert.map((g) => {
        const ids = new Set(g.liste.map((a) => a.id));
        const zeilen = g.liste.filter((a) => !a.eltern_id || !ids.has(a.eltern_id));
        return (
          <section key={g.id || g.name} className={s.abschnitt} aria-label={g.name}>
            <div className={s.abschnittKopf}>
              <h2>{g.name}</h2>
              <span className={s.meta}>{g.liste.length}</span>
            </div>
            <AufgabenListe
              liste={zeilen}
              label={g.name}
              zeigeOrt={gruppierung !== "ort"}
              zeigeEltern
              onUebernehmen={sa.uebernehmen}
              neu={neuFuer(g.id)}
              {...gemeinsam}
            />
          </section>
        );
      })}
      <Rueckfrage text={sa.rueckfrage?.text ?? null} onAntwort={(mit) => sa.rueckfrageAntwort(mit)} onAbbrechen={sa.rueckfrageSchliessen} />
      {loeschen.rueckfrage}
    </GruppenSeite>
  );
}

export const Offen = () => <Alle nurOffen />;
